import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  MapPin, 
  Calendar, 
  DollarSign, 
  Clock, 
  FileText,
  Send,
  CheckCircle,
  AlertCircle,
  Star,
  Users,
  Leaf,
  Eye
} from 'lucide-react';

const VerifierMarketplace = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    projectType: '',
    location: '',
    budgetRange: '',
    deadline: '',
    verificationStandard: '',
    sortBy: 'newest'
  });
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [proposalData, setProposalData] = useState({
    price: '',
    timeline: '',
    methodology: '',
    deliverables: '',
    experience: '',
    references: ''
  });

  // Mock data for projects seeking verification
  const mockProjects = [
    {
      id: 1,
      title: "Mangrove Restoration Thailand",
      description: "Large-scale mangrove restoration project covering 5,000 hectares along the Thai coast.",
      location: "Thailand",
      projectType: "Blue Carbon",
      creditsExpected: 75000,
      budgetRange: "$2,000 - $4,000",
      deadline: "2024-03-15",
      verificationStandard: "VCS",
      developer: "Coastal Conservation Initiative",
      status: "seeking-verification",
      postedDate: "2024-01-20",
      proposalsReceived: 3,
      requirements: [
        "Experience with blue carbon projects",
        "VCS methodology expertise",
        "Field verification capabilities",
        "Satellite monitoring analysis"
      ],
      documents: [
        "Project Design Document",
        "Baseline Study",
        "Community Engagement Plan"
      ]
    },
    {
      id: 2,
      title: "Biogas Plant India",
      description: "Agricultural waste-to-energy project generating clean electricity for rural communities.",
      location: "India",
      projectType: "Waste Management",
      creditsExpected: 45000,
      budgetRange: "$1,500 - $3,000",
      deadline: "2024-03-01",
      verificationStandard: "Gold Standard",
      developer: "Rural Energy Solutions",
      status: "seeking-verification",
      postedDate: "2024-01-18",
      proposalsReceived: 5,
      requirements: [
        "Gold Standard certification",
        "Biogas project experience",
        "Local language capabilities",
        "Social impact assessment"
      ],
      documents: [
        "Technical Specifications",
        "Environmental Impact Assessment",
        "Financial Analysis"
      ]
    },
    {
      id: 3,
      title: "Solar Microgrid Kenya",
      description: "Distributed solar energy system providing electricity to remote villages in Kenya.",
      location: "Kenya",
      projectType: "Renewable Energy",
      creditsExpected: 30000,
      budgetRange: "$1,000 - $2,500",
      deadline: "2024-02-28",
      verificationStandard: "CDM",
      developer: "African Solar Initiative",
      status: "seeking-verification",
      postedDate: "2024-01-15",
      proposalsReceived: 2,
      requirements: [
        "CDM methodology knowledge",
        "Renewable energy expertise",
        "Remote monitoring capabilities",
        "Community impact measurement"
      ],
      documents: [
        "Project Implementation Plan",
        "Technology Specifications",
        "Monitoring Plan"
      ]
    },
    {
      id: 4,
      title: "Forest Conservation Brazil",
      description: "REDD+ project protecting 15,000 hectares of Atlantic Forest through community partnerships.",
      location: "Brazil",
      projectType: "Forest Conservation",
      creditsExpected: 120000,
      budgetRange: "$5,000 - $8,000",
      deadline: "2024-04-01",
      verificationStandard: "CCBS",
      developer: "Atlantic Forest Foundation",
      status: "seeking-verification",
      postedDate: "2024-01-22",
      proposalsReceived: 1,
      requirements: [
        "REDD+ methodology expertise",
        "Portuguese language skills",
        "Biodiversity assessment capabilities",
        "Community engagement experience"
      ],
      documents: [
        "REDD+ Project Document",
        "Biodiversity Study",
        "Community Agreements"
      ]
    }
  ];

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setProjects(mockProjects);
      setFilteredProjects(mockProjects);
      setLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    filterProjects();
  }, [searchQuery, filters, projects]);

  const filterProjects = () => {
    let filtered = [...projects];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(project =>
        project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.projectType.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.developer.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Type filter
    if (filters.projectType) {
      filtered = filtered.filter(project => project.projectType === filters.projectType);
    }

    // Location filter
    if (filters.location) {
      filtered = filtered.filter(project => project.location === filters.location);
    }

    // Verification standard filter
    if (filters.verificationStandard) {
      filtered = filtered.filter(project => project.verificationStandard === filters.verificationStandard);
    }

    // Sort
    switch (filters.sortBy) {
      case 'budget-high':
        filtered.sort((a, b) => {
          const aMax = parseInt(a.budgetRange.split(' - $')[1].replace(',', ''));
          const bMax = parseInt(b.budgetRange.split(' - $')[1].replace(',', ''));
          return bMax - aMax;
        });
        break;
      case 'deadline':
        filtered.sort((a, b) => new Date(a.deadline) - new Date(b.deadline));
        break;
      case 'credits':
        filtered.sort((a, b) => b.creditsExpected - a.creditsExpected);
        break;
      case 'newest':
      default:
        filtered.sort((a, b) => new Date(b.postedDate) - new Date(a.postedDate));
        break;
    }

    setFilteredProjects(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const handleSubmitProposal = async (e) => {
    e.preventDefault();
    
    try {
      // Simulate API call
      console.log('Submitting proposal:', { projectId: selectedProject.id, ...proposalData });
      
      setTimeout(() => {
        alert('Proposal submitted successfully!');
        setShowProposalModal(false);
        setProposalData({
          price: '',
          timeline: '',
          methodology: '',
          deliverables: '',
          experience: '',
          references: ''
        });
        
        // Update project proposals count
        setProjects(prev => prev.map(p => 
          p.id === selectedProject.id 
            ? { ...p, proposalsReceived: p.proposalsReceived + 1 }
            : p
        ));
      }, 1000);
    } catch (error) {
      console.error('Error submitting proposal:', error);
      alert('Error submitting proposal. Please try again.');
    }
  };

  const projectTypes = [...new Set(projects.map(p => p.projectType))];
  const locations = [...new Set(projects.map(p => p.location))];
  const verificationStandards = [...new Set(projects.map(p => p.verificationStandard))];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Verification Marketplace</h1>
            <p className="text-gray-600">Find carbon credit projects seeking verification services</p>
          </div>
          <Link
            to="/verifier/dashboard"
            className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-2 px-4 rounded-lg transition duration-200"
          >
            Back to Dashboard
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Available Projects</p>
                <p className="text-2xl font-bold text-gray-900">{projects.length}</p>
              </div>
              <FileText className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Credits</p>
                <p className="text-2xl font-bold text-gray-900">{projects.reduce((sum, p) => sum + p.creditsExpected, 0).toLocaleString()}</p>
              </div>
              <Leaf className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg. Budget</p>
                <p className="text-2xl font-bold text-gray-900">$3.2K</p>
              </div>
              <DollarSign className="h-8 w-8 text-yellow-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Countries</p>
                <p className="text-2xl font-bold text-gray-900">{locations.length}</p>
              </div>
              <MapPin className="h-8 w-8 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
        <div className="flex flex-col lg:flex-row gap-4 items-center">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search projects, locations, or developers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            <select
              value={filters.projectType}
              onChange={(e) => handleFilterChange('projectType', e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">All Types</option>
              {projectTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>

            <select
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">All Locations</option>
              {locations.map(location => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>

            <select
              value={filters.verificationStandard}
              onChange={(e) => handleFilterChange('verificationStandard', e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">All Standards</option>
              {verificationStandards.map(standard => (
                <option key={standard} value={standard}>{standard}</option>
              ))}
            </select>

            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="newest">Newest</option>
              <option value="deadline">Deadline</option>
              <option value="budget-high">Budget: High to Low</option>
              <option value="credits">Credits: High to Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="mb-6">
        <p className="text-gray-600">
          Showing {filteredProjects.length} of {projects.length} projects
        </p>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {filteredProjects.map((project) => (
          <div key={project.id} className="bg-white rounded-lg shadow-sm border hover:shadow-lg transition-shadow duration-300">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-3">
                      {project.projectType}
                    </span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {project.verificationStandard}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {project.title}
                  </h3>
                </div>

                <div className="text-right">
                  <p className="text-sm text-gray-600">Budget</p>
                  <p className="font-semibold text-green-600">{project.budgetRange}</p>
                </div>
              </div>

              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {project.description}
              </p>

              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <MapPin className="h-4 w-4 mr-2" />
                  {project.location}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar className="h-4 w-4 mr-2" />
                  Deadline: {new Date(project.deadline).toLocaleDateString()}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Users className="h-4 w-4 mr-2" />
                  {project.developer}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <FileText className="h-4 w-4 mr-2" />
                  {project.creditsExpected.toLocaleString()} credits expected
                </div>
              </div>

              <div className="mb-4">
                <h4 className="font-medium text-gray-900 mb-2">Requirements</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {project.requirements.slice(0, 3).map((req, index) => (
                    <li key={index} className="flex items-center">
                      <CheckCircle className="h-3 w-3 text-green-500 mr-2 flex-shrink-0" />
                      {req}
                    </li>
                  ))}
                  {project.requirements.length > 3 && (
                    <li className="text-blue-600 text-xs">+{project.requirements.length - 3} more requirements</li>
                  )}
                </ul>
              </div>

              <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{project.proposalsReceived}</span> proposals received
                </div>
                <div className="text-sm text-gray-600">
                  Posted {new Date(project.postedDate).toLocaleDateString()}
                </div>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setSelectedProject(project);
                    // You would typically navigate to a detailed view here
                  }}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 text-center py-2 px-4 rounded-lg font-medium transition duration-200 flex items-center justify-center"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  View Details
                </button>
                <button
                  onClick={() => {
                    setSelectedProject(project);
                    setShowProposalModal(true);
                  }}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white text-center py-2 px-4 rounded-lg font-medium transition duration-200 flex items-center justify-center"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Submit Proposal
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Proposal Modal */}
      {showProposalModal && selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Submit Verification Proposal</h2>
              <p className="text-gray-600">{selectedProject.title}</p>
            </div>
            
            <form onSubmit={handleSubmitProposal} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Proposed Price (USD) *
                  </label>
                  <input
                    type="number"
                    required
                    value={proposalData.price}
                    onChange={(e) => setProposalData(prev => ({ ...prev, price: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="e.g., 2500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Timeline (days) *
                  </label>
                  <input
                    type="number"
                    required
                    value={proposalData.timeline}
                    onChange={(e) => setProposalData(prev => ({ ...prev, timeline: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="e.g., 45"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Verification Methodology *
                </label>
                <textarea
                  required
                  value={proposalData.methodology}
                  onChange={(e) => setProposalData(prev => ({ ...prev, methodology: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Describe your verification approach and methodology..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Deliverables *
                </label>
                <textarea
                  required
                  value={proposalData.deliverables}
                  onChange={(e) => setProposalData(prev => ({ ...prev, deliverables: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="List the deliverables you will provide..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relevant Experience *
                </label>
                <textarea
                  required
                  value={proposalData.experience}
                  onChange={(e) => setProposalData(prev => ({ ...prev, experience: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Describe your relevant experience for this project type..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  References (Optional)
                </label>
                <textarea
                  value={proposalData.references}
                  onChange={(e) => setProposalData(prev => ({ ...prev, references: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Provide contact information for references..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => setShowProposalModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
                >
                  Submit Proposal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {filteredProjects.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Search className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No projects found</h3>
          <p className="text-gray-600">Try adjusting your search criteria or filters</p>
        </div>
      )}
    </div>
  );
};

export default VerifierMarketplace; 